import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateAutomationTemplateDraftStateQuery } from '../../queries/useAutomationTemplateHasDraftQuery';
import { AUTOMATION_QUERY_KEYS, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';

interface Params {
	templateId: string;
	stepId: string;
	caseData: Api.IAutomationStepCaseStatement;
	impersonationContext?: Api.IImpersonationContext;
	relatedTemplateIds?: string[];
}

export function useAutomationTemplateStepSwitchAddCaseMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.ISwitchAutomationStep, variables: Params) => void;
	onError?: (error: Api.IOperationResultNoValue, variables: Params) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ templateId, stepId, caseData, impersonationContext }: Params) => {
			return userSession.webServiceHelper.callAsync<Api.ISwitchAutomationStep>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}/Step/Switch/${encodeURIComponent(stepId)}/Case`,
					impersonationContext,
				}),
				'POST',
				caseData
			);
		},
		onError,
		onSuccess: (data, variables) => {
			const templateIdsToInvalidate = new Set([variables.templateId, ...(variables.relatedTemplateIds ?? [])]);
			for (const templateId of templateIdsToInvalidate) {
				queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
				invalidateAutomationTemplateDraftStateQuery(templateId);
			}
			onSuccess?.(data, variables);
		},
	});
}
